import {
  Box,
  Flex,
  HStack,
  Icon,
  Image,
  Text,
  Tooltip,
} from "@chakra-ui/react";
import { ArrowLeft, ChevronLeft, ChevronRight } from "lucide-react";
import { memo } from "react";
import { Button } from "components/ds";
import type { MFCampaignReview } from "state/members/accessReviews";
import ResourceIcon from "../ResourceIcon";
import { appDisplayName } from "modules/apps/tenants";
import { STICKY_CHROME_PROPS } from "components/nav/stickyChrome";
import {
  buildNavigatorItems,
  navigatorDotLabel,
  navigatorNeighbours,
  reviewsLeftCount,
  reviewsLeftLabel,
} from "../reviewLogic";

/**
 * StickyNavigator — a slim floating pill on the review page (PLO-4803): an
 * icon-only Back, a prev/next pair of resource reviews with their logos, and a
 * centred row of position dots with a "N reviews left" readout. Each dot shows
 * progress as its fill (solid green = approved, hollow green = submitted and
 * waiting on the attestor, neutral = not reviewed yet) and position as an
 * azurite outline ring, so the review you are on still shows how far along it
 * is. Elevation defines it — white bg, elevLow, no border. The dot/prev/next/
 * left-count state come from reviewLogic so this stays presentational.
 */

const ResourceLogo = ({
  review,
  size = 18,
}: {
  review: Pick<MFCampaignReview, "resource">;
  size?: number;
}) => {
  const logo = review.resource?.logoUrl || review.resource?.imageUrl;
  return logo ? (
    <Image
      src={logo}
      alt={review.resource.name}
      width={`${size}px`}
      height={`${size}px`}
      borderRadius="4px"
      objectFit="cover"
      flexShrink={0}
    />
  ) : (
    <ResourceIcon resourceType={review.resource?.type || "app"} size={3} />
  );
};

export interface StickyNavigatorProps {
  reviews: MFCampaignReview[];
  currentReviewId: string;
  /** true while a ReviewStateAlert banner is on screen — the navigator yields
   *  its sticky slot to the alert (it takes visual priority). */
  hasBanner: boolean;
  onBack: () => void;
  onNavigate: (reviewId: string) => void;
}

const StickyNavigator = memo(
  ({
    reviews,
    currentReviewId,
    hasBanner,
    onBack,
    onNavigate,
  }: StickyNavigatorProps) => {
    const items = buildNavigatorItems(reviews, currentReviewId);
    const { prev, next } = navigatorNeighbours(reviews, currentReviewId);
    const left = reviewsLeftCount(reviews);

    return (
      <Box
        position={hasBanner ? "relative" : "sticky"}
        // Parks below the app's 68px sticky TopNav rather than under it.
        top={hasBanner ? undefined : "68px"}
        zIndex={9}
        {...STICKY_CHROME_PROPS}
        // Full width + stretch: this sits directly in the page column (its
        // parent has to span the page for sticky to travel), and that column
        // aligns its children to flex-start.
        w="full"
        alignSelf="stretch"
        pt="sm"
        pb="md"
        bg="bg.page"
      >
        <Flex
          position="relative"
          align="center"
          justify="space-between"
          gap="md"
          bg="bg.container"
          borderRadius="component.lg"
          boxShadow="elevLow"
          pl="xs"
          pr="2xs"
          py="2xs"
        >
          {/* left cluster: icon-only Back · divider · Previous review */}
          <HStack gap="xs">
            <Tooltip label="Back to Access Reviews" placement="top" hasArrow>
              <Button
                aria-label="Back to Access Reviews"
                icon={<Icon as={ArrowLeft} boxSize={4} />}
                variant="subtle"
                tone="neutral"
                size="sm"
                onClick={onBack}
              />
            </Tooltip>
            <Box w="1px" h="20px" bg="border.default" flexShrink={0} />
            {prev ? (
              <Button
                variant="subtle"
                tone="neutral"
                size="sm"
                leadingIcon={
                  <HStack gap="2xs">
                    <Icon as={ChevronLeft} boxSize={4} />
                    <ResourceLogo review={prev} />
                  </HStack>
                }
                onClick={() => onNavigate(prev.id)}
              >
                {prev.resource?.name ? appDisplayName(prev.resource) : prev.name}
              </Button>
            ) : (
              <Box w="1px" />
            )}
          </HStack>

          {/* centre: position dots + reviews-left readout, absolutely centred so
              it stays put as the side buttons' widths change. */}
          <HStack
            position="absolute"
            left="50%"
            top="50%"
            transform="translate(-50%, -50%)"
            gap="sm"
          >
            <HStack gap="2xs">
              {items.map((item) => {
                // "submitted" is a ring, not a fill: the reviewer is done with
                // it but the attestor still holds it, so it reads as half-done
                // next to a solid approved dot (PLO-5595). borderWidth on a
                // border-box 8px keeps every dot the same size and pitch.
                const submitted = item.dot === "submitted";
                const label = navigatorDotLabel(
                  item.dot,
                  item.name,
                  item.isCurrent,
                );
                return (
                  <Tooltip key={item.id} label={label} placement="top" hasArrow>
                    <Box
                      // A navigable dot IS a control, so it has to be a real
                      // button: an aria-label on a bare Box is not focusable
                      // and generic elements are not announced, which left the
                      // whole strip — and the state names below — reachable by
                      // pointer only. The rest are status indicators with
                      // nothing to activate, so they take role="img", which
                      // exposes the label without inventing a fake tab stop.
                      // Focus also opens the Tooltip, so the state name reaches
                      // keyboard users the same way hovering reaches everyone
                      // else.
                      as={item.navigable ? "button" : undefined}
                      type={item.navigable ? "button" : undefined}
                      role={item.navigable ? undefined : "img"}
                      aria-label={label}
                      w="8px"
                      h="8px"
                      p={0}
                      appearance="none"
                      borderRadius="full"
                      flexShrink={0}
                      bg={
                        item.dot === "complete"
                          ? "success.500"
                          : submitted
                            ? "bg.container"
                            : "neutral.300"
                      }
                      // Longhands, not the `border` shorthand: `border: none`
                      // would set border-style and silently kill the ring.
                      borderStyle={submitted ? "solid" : "none"}
                      borderWidth={submitted ? "2px" : "0"}
                      borderColor={submitted ? "success.500" : undefined}
                      // Position is drawn OUTSIDE the dot so it never competes
                      // with the progress fill — the review you are on keeps
                      // showing how far along it is (PLO-5595). outline is not
                      // laid out, and 2px at 1px offset clears the 4px gap.
                      // Never fights the focus ring: the current dot is by
                      // definition not navigable, so it is never focusable.
                      outline={item.isCurrent ? "2px solid" : undefined}
                      outlineColor={item.isCurrent ? "p.500" : undefined}
                      outlineOffset={item.isCurrent ? "1px" : undefined}
                      cursor={item.navigable ? "pointer" : "default"}
                      transition="transform 0.12s ease"
                      _hover={
                        item.navigable ? { transform: "scale(1.6)" } : undefined
                      }
                      _focusVisible={{
                        outline: "none",
                        boxShadow: "focusRing",
                        transform: "scale(1.6)",
                      }}
                      onClick={
                        item.navigable ? () => onNavigate(item.id) : undefined
                      }
                    />
                  </Tooltip>
                );
              })}
            </HStack>
            <Text
              fontSize="sm"
              fontWeight={500}
              color="text.body.secondary"
              whiteSpace="nowrap"
            >
              {reviewsLeftLabel(left)}
            </Text>
          </HStack>

          {/* right: Next review */}
          {next ? (
            <Button
              variant="subtle"
              tone="neutral"
              size="sm"
              leadingIcon={<ResourceLogo review={next} />}
              trailingIcon={<Icon as={ChevronRight} boxSize={4} />}
              onClick={() => onNavigate(next.id)}
            >
              {next.resource?.name ? appDisplayName(next.resource) : next.name}
            </Button>
          ) : (
            <Box w="1px" />
          )}
        </Flex>
      </Box>
    );
  },
);

StickyNavigator.displayName = "StickyNavigator";

export default StickyNavigator;
